if (!customElements.get("quick-add-modal")) {
  customElements.define(
    "quick-add-modal",
    class QuickAddModal extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector("dialog");
        this.isBundle = this.dataset.isBundle === "true";
        this._injected = false;

        if (this.isBundle) {
          this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
          this.updateSetPrice();
        }

        this.querySelector(".quick-add-modal__toggle")?.addEventListener(
          "click",
          () => this.hide()
        );

        // Close when clicking the backdrop (dialog element itself, outside content)
        this.dialog?.addEventListener("click", (event) => {
          if (event.target === this.dialog) this.hide();
        });

        // Cleanup fires on Escape, close(), or form[method=dialog]
        this.dialog?.addEventListener("close", () => {
          if (this.isBundle && this.modalContent) {
            this.modalContent.innerHTML = "";
          } else {
            const target = this.querySelector(".variant-selector-injection");
            if (target) {
              target.innerHTML = "";
              this._injected = false;
            }
          }

          const cart = document.querySelector("cart-drawer");
          if (cart && !cart.isOpen)
            document.body.classList.remove("overflow-hidden");
          document.body.dispatchEvent(new CustomEvent("modalClosed"));
          if (this._opener)
            requestAnimationFrame(() => this._opener.focus());
        });

        // Wire up trigger buttons pointing to this modal via aria-controls
        document
          .querySelectorAll(`[aria-controls="${this.id}"]`)
          .forEach((btn) =>
            btn.addEventListener("click", () => this.show(btn))
          );
      }

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        if (cartNotification) cartNotification.setActiveElement(this._opener);
        if (preventFocus) this._opener = null;
        this.dialog?.close();
        if (window.screen.height < 668) this.style.height = "auto";
      }

      show(opener) {
        this._opener = opener;

        if (this.isBundle) {
          this._showBundle(opener);
        } else {
          this._showInline();
        }
      }

      _showInline() {
        // Lazy-inject the variant selector from its template on first open
        if (!this._injected) {
          const tpl = this.querySelector("template[data-variant-selector]");
          const target = this.querySelector(".variant-selector-injection");
          if (tpl && target) {
            target.appendChild(tpl.content.cloneNode(true));
            this._injected = true;
          }
        }

        document.body.classList.add("overflow-hidden");
        this.dialog.showModal();

        // Focus first variant option, falling back to close button
        requestAnimationFrame(() => {
          const focusTarget =
            this.querySelector(
              ".variant-selector-injection [data-option] a"
            ) ||
            this.querySelector(".quick-add-modal__toggle") ||
            this.dialog;
          focusTarget?.focus?.();
        });
      }

      _showBundle(opener) {
        opener.setAttribute("aria-disabled", true);
        opener.classList.add("loading");
        opener
          .querySelector(".loading-overlay__spinner")
          ?.classList.remove("hidden");

        const cleanup = () => {
          opener.removeAttribute("aria-disabled");
          opener.classList.remove("loading");
          opener
            .querySelector(".loading-overlay__spinner")
            ?.classList.add("hidden");
        };

        fetch(`${opener.getAttribute("data-product-url")}`)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.productElement = responseHTML.querySelector(
              'section[id^="MainProduct-"]'
            );
            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.removeGalleryListSemantic();
            this.preventVariantURLSwitching();
            this.selectCartProducts();

            document.body.classList.add("overflow-hidden");
            this.dialog.showModal();
          })
          .finally(cleanup);
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;
        // Reinject script tags so they execute (innerHTML disables them by default)
        element.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector(
          "variant-radios,variant-selects"
        );
        if (!variantPicker) return;
        variantPicker.setAttribute("data-update-url", "false");
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector(
          "pickup-availability"
        );
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector("product-modal");
        if (productModal) productModal.remove();

        const modalDialog =
          this.productElement.querySelectorAll("modal-dialog");
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        this.productElement
          .querySelector(".vbb-set__details__sticky")
          ?.remove();
        this.productElement
          .querySelector(".vbb-set__details__description")
          ?.remove();
        this.productElement.querySelector(".vbb-set__carousel")?.remove();
        this.productElement
          .querySelector(".sticky-back-top__wrapper")
          ?.remove();
        this.productElement
          .querySelector(".vbb-product-details__details-container--mobile")
          ?.remove();
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickadd-${sectionId}`
          );
        this.productElement
          .querySelectorAll("variant-selects, variant-radios")
          .forEach((variantSelect) => {
            variantSelect.dataset.originalSection = sectionId;
          });
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector(
          '[id^="Slider-Gallery"]'
        );
        if (!galleryList) return;
        galleryList.setAttribute("role", "presentation");
        galleryList
          .querySelectorAll('[id^="Slide-"]')
          .forEach((li) => li.setAttribute("role", "presentation"));
      }

      selectCartProducts() {
        this.cartJson =
          JSON.parse(
            document.querySelector("script#cart-upsell-json")?.innerText
          ) ?? null;

        if (!this.cartJson) return;
        const bundleFormItems = this.querySelectorAll(
          ".vbb-set__details__item"
        );
        bundleFormItems.forEach((bundleFormItem, index) => {
          const productInCart = this.cartJson.items.findIndex(
            (item) =>
              item.product_id == bundleFormItem.dataset.activeProductId &&
              item.quantity > 0
          );
          if (productInCart > -1) {
            this.cartJson.items[productInCart].quantity -= 1;
            if (bundleFormItem.dataset.hasOnlyDefaultVariant === "true") {
              bundleFormItem
                .querySelector(".vbb-set__details__item--button")
                .click();
            } else {
              let item = bundleFormItems[index].querySelector(
                `li[data-variant-id="${this.cartJson.items[productInCart].variant_id}"]`
              );
              if (item) {
                item.click();
              }
            }
          }
        });
        const button = this.querySelector(
          ".vbb-set__details__atc button span:last-of-type"
        );
        if (button) {
          button.innerText = " - complete the bundle";
        }
      }

      async updateSetPrice() {
        const response = await fetch(this.getAttribute("data-product-url"));
        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const vbbSetForm = html.querySelector("vbb-set-form");
        if (!vbbSetForm) return;
        const { hasVariablePrice, customBundle, currencySymbol } =
          vbbSetForm.dataset;
        const isCustomBundle = customBundle === "true";
        const spinnerId = this.dataset.spinner;
        let discountedPrice, preDiscountedPrice;

        if (isCustomBundle) {
          const minimumItems = vbbSetForm.querySelector("[data-minimum-items]")
            .dataset.minimumItems;
          const items = Array.from(
            html.querySelectorAll("vbb-set-form .vbb-set__details__item")
          );
          const itemPrices = items.map((item) =>
            parseFloat(item.dataset.price)
          );
          const itemDiscountedPrices = items.map((item) =>
            parseFloat(item.dataset.discountedPrice)
          );

          preDiscountedPrice = this.calculateTotalPrice(itemPrices);
          discountedPrice = this.calculateTotalPrice(
            itemDiscountedPrices,
            minimumItems
          );
        } else {
          discountedPrice = html.querySelector(
            ".vbb-set__details__price--discounted"
          ).innerText;
          preDiscountedPrice = html.querySelector(
            ".vbb-set__details__price--prediscounted"
          )?.innerText;
        }

        this.updatePriceElements(spinnerId, {
          discountedPrice,
          preDiscountedPrice,
          currencySymbol,
          hasVariablePrice,
          isCustomBundle,
        });
      }

      calculateTotalPrice(prices, limit = prices.length) {
        return prices
          .sort()
          .slice(0, limit)
          .reduce((a, b) => parseFloat(a) + parseFloat(b), 0)
          .toFixed(2);
      }

      updatePriceElements(
        id,
        {
          discountedPrice,
          preDiscountedPrice,
          currencySymbol,
          hasVariablePrice,
          isCustomBundle,
        }
      ) {
        const priceHtml = `
        <div class="price price--sold-out price--on-sale">
          <div class="price__container">
            <div class="price__sale">
              <span class="visually-hidden visually-hidden--inline">Sale price</span>
              <span class="price-item price-item--sale price-item--last">${
                hasVariablePrice || isCustomBundle ? "From " : ""
              }${isCustomBundle ? currencySymbol : ""}${discountedPrice}</span>
              ${
                preDiscountedPrice != null
                  ? `<span class="visually-hidden visually-hidden--inline">Regular price</span>
                    <span><s class="price-item price-item--regular">${
                      isCustomBundle ? currencySymbol : ""
                    }${preDiscountedPrice}</s></span>`
                  : ""
              }
            </div>
          </div>
        </div>
      `;

        document
          .querySelectorAll(`#${id} .bundle-upsell-price__placeholder`)
          .forEach((el) => (el.innerHTML = priceHtml));
      }
    }
  );
}
